/**
 * Where the access token lives.
 *
 * The Dawn Patrol API authenticates with `Authorization: Bearer <token>`
 * (passport-http-bearer on the Express side; the GraphQL handler reads the same
 * header). It does not use cookies, so there is nothing the browser attaches
 * for us — every request has to carry the header itself.
 *
 * **In memory only, on purpose.** A token in `localStorage` is readable by any
 * script that ends up on the page, and this app will host an OAuth consent
 * screen, which makes it a high-value target. In-memory means a refresh loses
 * the session; that is the correct trade-off until there is a refresh-token
 * flow to restore it.
 *
 * Whether anything is persisted, and how a session survives a reload, is a
 * decision for the consent-shell work — not something to settle by accident
 * here.
 */

let accessToken: string | null = null;

type Listener = (token: string | null) => void;
const listeners = new Set<Listener>();

export function getAccessToken(): string | null {
    return accessToken;
}

export function setAccessToken(token: string | null): void {
    accessToken = token;
    for (const listener of listeners) {
        listener(token);
    }
}

export function clearAccessToken(): void {
    setAccessToken(null);
}

/** Subscribe to token changes. Returns an unsubscribe function. */
export function onAccessTokenChange(listener: Listener): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
}
